import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Wallet, Plus, ArrowUpRight, ArrowDownRight, Clock, 
  CreditCard, Zap, ChevronRight, AlertCircle, CheckCircle,
  Loader2, Minus, BanknoteIcon, Coins, DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import WalletBreakdown from '../components/WalletBreakdown';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WalletPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [paymentUsername, setPaymentUsername] = useState('');
  const [withdrawPaymentMethod, setWithdrawPaymentMethod] = useState('cashapp'); // cashapp or venmo
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [convertAmount, setConvertAmount] = useState('');
  const [config, setConfig] = useState(null);
  const [depositLoading, setDepositLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [convertLoading, setConvertLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [withdrawalFeeInfo, setWithdrawalFeeInfo] = useState({ fee: 0, net_amount: 0 });

  useEffect(() => {
    fetchData();
    
    // Check for payment completion
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [txnRes, wdRes, configRes] = await Promise.all([
        axios.get(`${API}/wallet/transactions?limit=5`, { withCredentials: true }),
        axios.get(`${API}/wallet/withdrawals`, { withCredentials: true }),
        axios.get(`${API}/matches/config`, { withCredentials: true })
      ]);
      setTransactions(txnRes.data.transactions || []);
      setWithdrawals(wdRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please check your email for confirmation.');
      navigate('/wallet', { replace: true });
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await axios.get(
        `${API}/wallet/deposit/status/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.payment_status === 'paid') {
        const rate = config?.credit_conversion_rate || 100;
        const creditsReceived = response.data.amount * rate;
        toast.success(`Successfully purchased ${creditsReceived.toLocaleString()} credits!`);
        await refreshUser();
        await fetchData();
        navigate('/wallet', { replace: true });
        setCheckingPayment(false);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        navigate('/wallet', { replace: true });
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error checking payment status');
      setCheckingPayment(false);
    }
  };

  const handleDeposit = async () => {
    const amount = parseFloat(depositAmount);
    
    const minDeposit = config?.min_deposit || 5;
    const maxDeposit = config?.max_deposit || 1000;
    
    if (!amount || amount < minDeposit) {
      toast.error(`Minimum purchase is $${minDeposit}`);
      return;
    }
    if (amount > maxDeposit) {
      toast.error(`Maximum purchase is $${maxDeposit}`);
      return;
    }

    setDepositLoading(true);

    try {
      const response = await axios.post(
        `${API}/wallet/deposit`,
        { 
          amount: amount,
          origin_url: window.location.origin 
        },
        { withCredentials: true }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate purchase');
      setDepositLoading(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    
    const minWithdraw = config?.min_withdrawal || 20;
    const maxWithdraw = config?.max_withdrawal || 500;
    
    if (!amount || amount < minWithdraw) {
      toast.error(`Minimum withdrawal is $${minWithdraw}`);
      return;
    }
    if (amount > maxWithdraw) {
      toast.error(`Maximum withdrawal is $${maxWithdraw}`);
      return;
    }
    if (amount !== Math.floor(amount)) {
      toast.error('Withdrawal amount must be a whole number');
      return;
    }
    if (amount > (user?.cash_balance || 0)) {
      toast.error('Insufficient cash balance');
      return;
    }
    if (!paymentUsername.trim()) {
      const methodName = withdrawPaymentMethod === 'venmo' ? 'Venmo' : 'Cash App';
      toast.error(`Please enter your ${methodName} username`);
      return;
    }
    if (!paymentConfirmed) {
      const methodName = withdrawPaymentMethod === 'venmo' ? 'Venmo' : 'Cash App';
      toast.error(`Please confirm your ${methodName} username is correct`);
      return;
    }

    setWithdrawLoading(true);

    try {
      await axios.post(
        `${API}/wallet/withdraw`,
        { 
          amount: amount, 
          payment_method: withdrawPaymentMethod,
          payment_username: paymentUsername.trim()
        },
        { withCredentials: true }
      );

      const methodName = withdrawPaymentMethod === 'venmo' ? 'Venmo' : 'Cash App';
      toast.success(`Withdrawal request submitted for $${amount.toFixed(2)} via ${methodName}`);
      setWithdrawAmount('');
      setPaymentUsername('');
      setPaymentConfirmed(false);
      setWithdrawalFeeInfo({ fee: 0, net_amount: 0 });
      await refreshUser();
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request withdrawal');
    } finally {
      setWithdrawLoading(false);
    }
  };

  // Calculate withdrawal fee in real-time
  const calculateWithdrawalFee = (amount) => {
    const feePercent = config?.withdrawal_fee_percent || 1.5;
    const feeCap = config?.withdrawal_fee_cap || 10;
    
    let fee = amount * (feePercent / 100);
    if (fee > feeCap) fee = feeCap;
    
    return {
      fee: Math.round(fee * 100) / 100,
      net_amount: Math.round((amount - fee) * 100) / 100
    };
  };

  // Update fee info when amount changes
  const handleWithdrawAmountChange = (value) => {
    setWithdrawAmount(value);
    const amount = parseFloat(value) || 0;
    if (amount >= 20) {
      setWithdrawalFeeInfo(calculateWithdrawalFee(amount));
    } else {
      setWithdrawalFeeInfo({ fee: 0, net_amount: 0 });
    }
  };

  const handleConvert = async () => {
    const amount = parseFloat(convertAmount);
    
    if (!amount || amount < 5) {
      toast.error('Minimum conversion is $5.00');
      return;
    }
    
    if (amount > cashBalance) {
      toast.error('Insufficient cash balance');
      return;
    }

    setConvertLoading(true);

    try {
      const response = await axios.post(
        `${API}/wallet/convert-cash-to-credits`,
        { amount: amount },
        { withCredentials: true }
      );

      toast.success(`Converted $${amount.toFixed(2)} to ${response.data.credits_received.toLocaleString()} credits!`);
      setConvertAmount('');
      setShowConvertModal(false);
      await refreshUser();
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Conversion failed');
    } finally {
      setConvertLoading(false);
    }
  };

  const quickAmounts = [10, 25, 50, 100];
  const withdrawAmounts = [20, 50, 100];  // Updated: removed $10
  const creditsBalance = user?.credits_balance || 0;
  const cashBalance = user?.cash_balance || 0;
  const creditRate = config?.credit_conversion_rate || 100;

  // Calculate withdrawal breakdown
  const calculateFees = (amount) => {
    const fixed = config?.withdrawal_fee_fixed || 0;
    const percent = config?.withdrawal_fee_percent || 0;
    const numAmount = parseFloat(amount) || 0;
    const fee = fixed + (numAmount * (percent / 100));
    const net = Math.max(0, numAmount - fee);
    return { fee, net };
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="wallet-page">
        {/* Checking Payment Overlay */}
        {checkingPayment && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
            <div className="glass-card rounded-xl p-8 text-center">
              <Loader2 className="w-12 h-12 text-[#00F0FF] animate-spin mx-auto mb-4" />
              <p className="font-heading font-semibold">Processing Purchase...</p>
              <p className="text-white/50 text-sm mt-2">Please wait while we confirm your credit purchase</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">Wallet</h1>
          <p className="text-white/50">Manage your balance and winnings</p>
        </div>

        {/* Wallet Breakdown Component - Full Overview */}
        <div className="mb-8">
          <WalletBreakdown />
        </div>

        {/* Dual Balance Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Match Balance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-2xl p-6 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-[#00F0FF]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#00F0FF]/20 flex items-center justify-center">
                  <Coins className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Match Balance</p>
                  <p className="text-white/30 text-xs">Available for match entry</p>
                </div>
              </div>
              <p 
                className="font-mono text-3xl font-bold text-[#00F0FF]"
                style={{ textShadow: '0 0 30px rgba(0, 240, 255, 0.4)' }}
                data-testid="credits-balance"
              >
                ${(creditsBalance / creditRate).toFixed(2)}
              </p>
            </div>
          </motion.div>

          {/* Winnings Wallet */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="glass-card rounded-2xl p-6 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-[#CCFF00]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#CCFF00]/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-[#CCFF00]" />
                </div>
                <div>
                  <p className="text-white/50 text-sm">Winnings</p>
                  <p className="text-white/30 text-xs">Available for withdrawal</p>
                </div>
              </div>
              <p 
                className="font-mono text-3xl font-bold text-[#CCFF00]"
                style={{ textShadow: '0 0 30px rgba(204, 255, 0, 0.4)' }}
                data-testid="cash-balance"
              >
                ${cashBalance.toFixed(2)}
              </p>
              
              {/* Convert to Match Balance Button */}
              {cashBalance >= 5 && (
                <Button
                  onClick={() => setShowConvertModal(true)}
                  variant="outline"
                  className="w-full mt-4 border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                  data-testid="convert-to-credits-btn"
                >
                  <Coins className="w-4 h-4 mr-2" />
                  Add to Match Balance
                </Button>
              )}
            </div>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Deposit Money Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00D632]/20 flex items-center justify-center">
                <Plus className="w-5 h-5 text-[#00D632]" />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-lg">Deposit Money</h2>
                <p className="text-white/50 text-sm">Deposit money to your MatchStake balance to enter matches</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Deposit Tiers Preview */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { usd: 5 },
                  { usd: 10 },
                  { usd: 20 },
                  { usd: 50 },
                ].map((tier) => (
                  <div key={tier.usd} className="p-3 rounded-lg bg-white/5 text-center">
                    <p className="font-mono text-lg font-bold text-[#CCFF00]">${tier.usd}</p>
                    <p className="text-white/40 text-xs">deposit</p>
                  </div>
                ))}
              </div>

              <div className="p-3 rounded-lg bg-[#00D632]/10 border border-[#00D632]/30 flex items-center gap-3">
                <img src="/cashapp-qr.jpg" alt="Cash App" className="w-12 h-12 rounded-lg" />
                <div className="flex-1">
                  <p className="font-semibold text-[#00D632]">Pay with Cash App</p>
                  <p className="text-white/50 text-sm">Send to $MatchStakeApp</p>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-[#3D95CE]/10 border border-[#3D95CE]/30 flex items-center gap-3">
                <img src="/venmo-qr.jpg" alt="Venmo" className="w-12 h-12 rounded-lg" />
                <div className="flex-1">
                  <p className="font-semibold text-[#3D95CE]">Pay with Venmo</p>
                  <p className="text-white/50 text-sm">Send to @matchstakeapp</p>
                </div>
              </div>

              <Button
                onClick={() => navigate('/add-credits')}
                className="w-full bg-[#00D632] hover:bg-[#00B82A] text-black font-bold py-6"
                data-testid="add-credits-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Deposit Money
              </Button>

              <p className="text-xs text-white/40 text-center">Minimum deposit: $5</p>
            </div>
          </motion.div>

          {/* Withdraw Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="glass-card rounded-xl p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#FFB800]/20 flex items-center justify-center">
                <BanknoteIcon className="w-5 h-5 text-[#FFB800]" />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-lg">Withdraw Money</h2>
                <p className="text-white/50 text-sm">Withdraw your winnings anytime</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-white/50 mb-2 block">Amount (USD)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 font-mono">$</span>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    className="pl-8 input-dark text-lg font-mono"
                    min="25"
                    max={cashBalance}
                    step="0.01"
                    data-testid="withdraw-amount-input"
                  />
                </div>
              </div>

              {/* Cash App Username */}
              <div>
                <label className="text-sm text-white/50 mb-2 block">
                  {withdrawPaymentMethod === 'venmo' ? 'Venmo' : 'Cash App'} Username
                </label>
                <div className="relative">
                  <span className={`absolute left-4 top-1/2 -translate-y-1/2 font-mono ${
                    withdrawPaymentMethod === 'venmo' ? 'text-[#3D95CE]' : 'text-[#00D632]'
                  }`}>
                    {withdrawPaymentMethod === 'venmo' ? '@' : '$'}
                  </span>
                  <Input
                    type="text"
                    placeholder={withdrawPaymentMethod === 'venmo' ? 'YourVenmoUsername' : 'YourCashAppName'}
                    value={paymentUsername}
                    onChange={(e) => setPaymentUsername(e.target.value.replace(/^[@$]/, ''))}
                    className="pl-8 input-dark font-mono"
                    data-testid="payment-username-input"
                  />
                </div>
                <p className="text-xs text-white/40 mt-1">
                  Enter your {withdrawPaymentMethod === 'venmo' ? 'Venmo' : 'Cash App'} username (without the {withdrawPaymentMethod === 'venmo' ? '@' : '$'})
                </p>
              </div>

              {/* Payment Method Selection */}
              <div>
                <label className="text-sm text-white/50 mb-2 block">Select Withdrawal Method</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => { setWithdrawPaymentMethod('cashapp'); setPaymentConfirmed(false); }}
                    className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 ${
                      withdrawPaymentMethod === 'cashapp'
                        ? 'border-[#00D632] bg-[#00D632]/10'
                        : 'border-white/10 bg-white/5 hover:border-white/30'
                    }`}
                    data-testid="withdraw-method-cashapp"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#00D632] flex items-center justify-center">
                      <span className="text-white font-bold">$</span>
                    </div>
                    <span className="font-medium text-white text-sm">Cash App</span>
                  </button>
                  
                  <button
                    onClick={() => { setWithdrawPaymentMethod('venmo'); setPaymentConfirmed(false); }}
                    className={`p-3 rounded-xl border-2 transition-all flex items-center gap-2 ${
                      withdrawPaymentMethod === 'venmo'
                        ? 'border-[#3D95CE] bg-[#3D95CE]/10'
                        : 'border-white/10 bg-white/5 hover:border-white/30'
                    }`}
                    data-testid="withdraw-method-venmo"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#3D95CE] flex items-center justify-center">
                      <span className="text-white font-bold text-sm">V</span>
                    </div>
                    <span className="font-medium text-white text-sm">Venmo</span>
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {withdrawAmounts.map(amount => (
                  <Button
                    key={amount}
                    variant="outline"
                    size="sm"
                    onClick={() => handleWithdrawAmountChange(amount.toString())}
                    disabled={cashBalance < amount}
                    className={`btn-secondary ${withdrawAmount === amount.toString() ? 'border-[#FFB800] bg-[#FFB800]/10' : ''}`}
                  >
                    ${amount}
                  </Button>
                ))}
                {cashBalance >= 25 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleWithdrawAmountChange(Math.floor(cashBalance).toString())}
                    className="btn-secondary"
                  >
                    Max
                  </Button>
                )}
              </div>

              {/* Real-time Fee Calculation */}
              {parseFloat(withdrawAmount) >= 20 && (
                <div className="p-4 rounded-xl bg-[#FFB800]/10 border border-[#FFB800]/30 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Withdrawal Amount</span>
                    <span className="text-white font-mono">${parseFloat(withdrawAmount).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/50">Fee (1.5%, max $10)</span>
                    <span className="text-[#FF2E2E] font-mono">-${withdrawalFeeInfo.fee.toFixed(2)}</span>
                  </div>
                  <div className="pt-2 border-t border-white/10 flex justify-between">
                    <span className="font-semibold text-[#CCFF00]">You'll receive</span>
                    <span className="font-mono font-bold text-[#CCFF00] text-lg">${withdrawalFeeInfo.net_amount.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Payment Method Confirmation Checkbox */}
              <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paymentConfirmed}
                    onChange={(e) => setPaymentConfirmed(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-white/30 bg-white/10 text-[#00F0FF] focus:ring-[#00F0FF] focus:ring-offset-0"
                    data-testid="payment-confirm-checkbox"
                  />
                  <span className="text-sm text-white/70">
                    I confirm the {withdrawPaymentMethod === 'venmo' ? 'Venmo' : 'Cash App'} username is correct. I understand MatchStake is not responsible for funds sent to incorrect usernames.
                  </span>
                </label>
              </div>

              <div className="p-3 rounded-lg bg-white/5 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-white/50 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-white/50 space-y-1">
                  <p><strong>Available to withdraw:</strong> ${cashBalance.toFixed(2)}</p>
                  <p><strong>Withdrawal limits:</strong> ${config?.min_withdrawal || 20} - ${config?.max_withdrawal || 500}</p>
                  <p><strong>Fee:</strong> 1.5% (max $10)</p>
                  <p><strong>Processing time:</strong> 1-3 business days</p>
                  <p>Minimum withdrawal: $20</p>
                  <Link to="/withdrawal-policy" className="text-[#00F0FF] hover:underline">View Withdrawal Policy</Link>
                </div>
              </div>

              {parseFloat(withdrawAmount) > cashBalance && (
                <div className="p-3 rounded-lg bg-[#FF2E2E]/10 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-[#FF2E2E]" />
                  <span className="text-sm text-[#FF2E2E]">Insufficient cash balance</span>
                </div>
              )}

              <Button
                onClick={handleWithdraw}
                disabled={withdrawLoading || !withdrawAmount || !paymentUsername.trim() || !paymentConfirmed || parseFloat(withdrawAmount) > cashBalance || parseFloat(withdrawAmount) < 20}
                className="w-full bg-[#FFB800] text-black hover:bg-[#FFB800]/90 py-6"
                data-testid="withdraw-submit-btn"
              >
                {withdrawLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Minus className="w-4 h-4 mr-2" />
                    Request Withdrawal
                  </>
                )}
              </Button>

              {/* Pending Withdrawals */}
              {withdrawals.filter(w => w.status === 'pending').length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-sm text-white/50 mb-2">Pending Withdrawals</p>
                  {withdrawals.filter(w => w.status === 'pending').map(wd => (
                    <div key={wd.withdrawal_id} className="flex justify-between p-2 rounded bg-white/5">
                      <span className="text-sm text-white/70">{wd.withdrawal_id}</span>
                      <span className="font-mono text-[#FFB800]">${wd.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Transaction History - Full Width */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card rounded-xl p-6 mt-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#7000FF]/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-[#7000FF]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-heading font-semibold text-lg">Transaction History</h2>
                  <p className="text-white/50 text-sm">Your recent activity</p>
                </div>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/transactions')}
                  className="btn-secondary hidden sm:flex"
                >
                  View All
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-16 bg-white/5 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <p className="text-white/50">No transactions yet</p>
              <p className="text-white/30 text-sm mt-1">Make a deposit to get started</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2">
              {transactions.map(txn => (
                <div 
                  key={txn.transaction_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-white/5"
                  data-testid={`txn-${txn.transaction_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      txn.amount > 0 ? 'bg-[#00FF94]/20' : 'bg-[#FF2E2E]/20'
                    }`}>
                      {txn.amount > 0 ? (
                        <ArrowDownRight className="w-4 h-4 text-[#00FF94]" />
                      ) : (
                        <ArrowUpRight className="w-4 h-4 text-[#FF2E2E]" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium capitalize">
                        {txn.type.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xs text-white/40 truncate max-w-[150px]">
                        {txn.description}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-mono font-bold ${
                      txn.amount > 0 ? 'text-[#00FF94]' : 'text-[#FF2E2E]'
                    }`}>
                      {txn.amount > 0 ? '+' : ''}${Math.abs(txn.amount).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {transactions.length > 0 && (
            <div className="mt-4 text-center sm:hidden">
              <Button 
                variant="outline"
                className="w-full btn-secondary text-sm"
                onClick={() => navigate('/transactions')}
              >
                View All Transactions
              </Button>
            </div>
          )}
        </motion.div>
      </div>

      {/* Conversion Modal */}
      {showConvertModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card rounded-xl p-6 max-w-md w-full"
          >
            <h3 className="font-heading font-bold text-xl mb-4">Add to Match Balance</h3>
            <p className="text-white/70 text-sm mb-4">
              Move your winnings to your match balance
            </p>

            {/* Amount Input */}
            <div className="mb-4">
              <label className="text-sm text-white/50 mb-2 block">Amount (USD)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 font-mono">$</span>
                <Input
                  type="number"
                  value={convertAmount}
                  onChange={(e) => setConvertAmount(e.target.value)}
                  placeholder="0.00"
                  className="pl-8 input-dark text-lg font-mono"
                  min="5"
                  max={cashBalance}
                  step="0.01"
                  autoFocus
                  data-testid="convert-amount-input"
                />
              </div>
            </div>

            {/* Quick amounts */}
            <div className="flex flex-wrap gap-2 mb-4">
              {[5, 10, 25, 50].filter(amt => amt <= cashBalance).map(amount => (
                <Button
                  key={amount}
                  variant="outline"
                  size="sm"
                  onClick={() => setConvertAmount(amount.toString())}
                  className={`btn-secondary ${convertAmount === amount.toString() ? 'border-[#00F0FF] bg-[#00F0FF]/10' : ''}`}
                >
                  ${amount}
                </Button>
              ))}
              {cashBalance >= 5 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConvertAmount(cashBalance.toFixed(2))}
                  className={`btn-secondary ${parseFloat(convertAmount) === cashBalance ? 'border-[#00F0FF] bg-[#00F0FF]/10' : ''}`}
                >
                  Max (${cashBalance.toFixed(2)})
                </Button>
              )}
            </div>

            {/* Preview */}
            {convertAmount && parseFloat(convertAmount) >= 5 && (
              <div className="mb-4 p-4 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/20">
                <p className="text-sm text-white/70 mb-1">Adding to match balance:</p>
                <p className="font-mono font-bold text-[#00F0FF] text-2xl">
                  ${parseFloat(convertAmount).toFixed(2)}
                </p>
              </div>
            )}

            {/* Warning */}
            <div className="mb-4 p-3 rounded-lg bg-[#FFB800]/10 border border-[#FFB800]/20 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-[#FFB800] mt-0.5 flex-shrink-0" />
              <div className="text-xs text-[#FFB800]">
                <p className="font-semibold mb-1">Match Balance</p>
                <p>Funds moved to match balance can only be used for match entry fees.</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowConvertModal(false);
                  setConvertAmount('');
                }}
                variant="outline"
                className="flex-1 border-white/20"
                disabled={convertLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConvert}
                disabled={convertLoading || !convertAmount || parseFloat(convertAmount) < 5}
                className="flex-1 btn-primary"
                data-testid="confirm-convert-btn"
              >
                {convertLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Coins className="w-4 h-4 mr-2" />
                    Add to Balance
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default WalletPage;
